import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, type JSX } from "react";
import { Link } from "react-router";
import { Card } from "../../components/Card.tsx";
import { NewAccountForm } from "../../components/NewAccountForm.tsx";
import { ProgressIndicator } from "../../components/ProgressIndicator.tsx";
import { Account } from "../../db/models.ts";
import { useORPC } from "../../orpc-react.tsx";

export default function Dashboard(): JSX.Element {
  const orpc = useORPC();

  const usersQuery = useQuery(orpc.iam.users.queryOptions({ input: {} }));
  useEffect(() => {
    console.debug(`Users query data: `, usersQuery.data);
  }, [usersQuery.data]);

  const listAccountsQuery = useQuery(orpc.accounting.listAccounts.queryOptions({ input: {} }));

  const createAccountMutation = useMutation(
    orpc.accounting.createAccount.mutationOptions({
      onSuccess: () => listAccountsQuery.refetch(),
    }),
  );

  return (
    <>
      <Link to="import">Import from Google Sheets!</Link>
      <NewAccountForm onSubmit={(attrs) => createAccountMutation.mutateAsync(attrs)} />
      {listAccountsQuery.isLoading && <ProgressIndicator>Loading accounts...</ProgressIndicator>}
      {listAccountsQuery.isSuccess && (
        <>
          {listAccountsQuery.data.length === 0 ? (
            "You have no accounts saved."
          ) : (
            <table>
              <tbody>
                {listAccountsQuery.data.map((account) => (
                  <AccountRow
                    account={Account.parse({
                      ...account,
                      createdAt: new Date(account.createdAt),
                      updatedAt: new Date(account.updatedAt),
                    } satisfies Account)}
                    key={account.id}
                  />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      {listAccountsQuery.isError && <Card>{listAccountsQuery.error.message}</Card>}
    </>
  );
}

interface AccountRowProps {
  account: Account;
}
function AccountRow(props: AccountRowProps): JSX.Element {
  const { account } = props;

  const creation = account.creatorId
    ? `Created by ${account.creatorId} at ${account.createdAt.toISOString()}`
    : `Created at ${account.createdAt.toISOString()}`;

  const update =
    account.updatedAt.getTime() !== account.createdAt.getTime()
      ? `Last updated at ${account.updatedAt.toISOString()}`
      : undefined;

  return (
    <tr>
      <td title={update ? `${creation}\n${update}` : creation}>{account.name}</td>
    </tr>
  );
}
